import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Money, StatusBadge, RiskBadge, Spinner, EmptyState, fmtMoney, fmtCompact } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, ShieldCheck, Target, Headphones } from "lucide-react";

export default function Customer360() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { reportingCcy } = useAuth();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => { setD(null); setErr(null); api.get(`/customers/${id}/360?reporting_ccy=${reportingCcy}`).then((r) => setD(r.data)).catch((e) => setErr(e?.response?.status === 403 ? "You don't have access to this record." : "Unable to load this record.")); }, [id, reportingCcy]);
  if (err) return (<div><Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-3 gap-1" data-testid="back-btn"><ArrowLeft className="h-4 w-4" /> Back</Button><EmptyState title={err} hint="Return to the previous screen." /></div>);
  if (!d) return <Spinner />;
  const c = d.customer;

  return (
    <div>
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-3 gap-1" data-testid="back-btn"><ArrowLeft className="h-4 w-4" /> Back</Button>
      <PageHeader overline={`Customer 360 · ${c.code}`} title={c.name}
        subtitle={`${c.type} · ${c.nationality} resident in ${c.residence} · RM ${c.rm_name} · ${c.division}`}
        testid="customer360-header"
        actions={<div className="flex gap-2 items-center"><StatusBadge status={c.onboarding_status} /><RiskBadge rating={c.risk_rating} /></div>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="p-5 lg:col-span-2 card-elev relative overflow-hidden accent-line">
          <div className="overline-label text-muted-foreground">Investment AUM ({reportingCcy})</div>
          <div className="text-3xl font-num font-bold mt-2" data-testid="customer-aum-total"><Money value={d.aum_total_reporting} ccy={reportingCcy} /></div>
          <div className="flex items-center gap-2 mt-2">
            {d.gain_30d_reporting !== null && d.gain_30d_reporting !== undefined && (
              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-num font-semibold ${d.gain_30d_reporting >= 0 ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-rose-50 text-rose-700 border border-rose-200"}`} data-testid="customer-gain-pill">
                {d.gain_30d_reporting >= 0 ? "▲" : "▼"} {d.gain_30d_reporting >= 0 ? "+" : ""}{d.gain_30d_pct}% · {d.gain_30d_reporting >= 0 ? "+" : "−"}{fmtCompact(Math.abs(d.gain_30d_reporting), reportingCcy)} (30d)
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-2">Valuation {d.valuation_date} · source freshness T-1 (mock). Excludes insurance cover.</div>
        </Card>
        <Card className="p-5 card-elev"><div className="overline-label text-muted-foreground">Onboarding</div><div className="mt-2"><StatusBadge status={c.onboarding_status} /></div></Card>
        <Card className="p-5 card-elev"><div className="overline-label text-muted-foreground">Compliance</div><div className="mt-2"><StatusBadge status={c.compliance_status} /></div></Card>
      </div>

      <Tabs defaultValue="holdings">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="holdings" data-testid="tab-holdings">Holdings</TabsTrigger>
          <TabsTrigger value="insurance" data-testid="tab-insurance">Insurance</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
          <TabsTrigger value="relationship" data-testid="tab-relationship">Leads & Requests</TabsTrigger>
        </TabsList>

        <TabsContent value="holdings" className="mt-4 space-y-4">
          {d.asset_groups.length === 0 && <EmptyState title="No investment holdings" />}
          {d.asset_groups.map((g) => (
            <Card key={g.asset_class} className="p-5" data-testid={`asset-group-${g.asset_class}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold">{g.label}</h3>
                <div className="text-right"><div className="font-num font-bold"><Money value={g.value_reporting} ccy={reportingCcy} /></div>
                  <div className="text-xs text-muted-foreground">cost {fmtMoney(g.cost_reporting, reportingCcy)}</div></div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Holding</TableHead><TableHead>Folio/Acct</TableHead><TableHead className="text-right">Units</TableHead>
                    <TableHead className="text-right">Value (orig)</TableHead><TableHead className="text-right">Value ({reportingCcy})</TableHead>
                    <TableHead>Source</TableHead><TableHead>ARN</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {g.holdings.map((h) => (
                      <TableRow key={h.id}>
                        <TableCell className="font-medium">{h.name}</TableCell>
                        <TableCell className="font-num text-xs">{h.folio}</TableCell>
                        <TableCell className="text-right font-num">{h.units?.toLocaleString()}</TableCell>
                        <TableCell className="text-right font-num">{fmtMoney(h.value, h.value_ccy)}</TableCell>
                        <TableCell className="text-right font-num">{fmtMoney(h.value_reporting, reportingCcy)}</TableCell>
                        <TableCell className="text-xs">{h.source}</TableCell>
                        <TableCell className="font-num text-xs">{h.arn || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="insurance" className="mt-4">
          <Card className="p-5">
            <p className="text-xs text-amber-700 dark:text-amber-400 mb-3">{d.insurance_note}</p>
            {d.insurance.length === 0 ? <EmptyState title="No insurance policies" /> : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Policy</TableHead><TableHead>No.</TableHead><TableHead className="text-right">Sum Assured / Cover</TableHead>
                    <TableHead className="text-right">Premium</TableHead><TableHead className="text-right">Policy Value</TableHead>
                    <TableHead className="text-right">Value ({reportingCcy})</TableHead><TableHead>Source</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {d.insurance.map((p, i) => (
                      <TableRow key={i} data-testid={`insurance-row-${i}`}>
                        <TableCell className="font-medium">{p.name}</TableCell>
                        <TableCell className="font-num text-xs">{p.policy_no}</TableCell>
                        <TableCell className="text-right font-num">{fmtMoney(p.cover, p.value_ccy)}</TableCell>
                        <TableCell className="text-right font-num">{fmtMoney(p.premium, p.premium_ccy)}</TableCell>
                        <TableCell className="text-right font-num">{fmtMoney(p.policy_value, p.value_ccy)}</TableCell>
                        <TableCell className="text-right font-num">{fmtMoney(p.policy_value_reporting, reportingCcy)}</TableCell>
                        <TableCell className="text-xs">{p.source}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="transactions" className="mt-4">
          <Card className="p-5">
            {d.transactions.length === 0 ? <EmptyState title="No transactions" /> : (
              <Table>
                <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Scheme</TableHead><TableHead className="text-right">Units</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Source</TableHead></TableRow></TableHeader>
                <TableBody>
                  {d.transactions.map((t) => (
                    <TableRow key={t.id}><TableCell className="font-num text-xs">{t.date}</TableCell><TableCell>{t.type}</TableCell>
                      <TableCell>{t.scheme}</TableCell><TableCell className="text-right font-num">{t.units || "—"}</TableCell>
                      <TableCell className="text-right font-num">{fmtMoney(t.amount, t.currency)}</TableCell><TableCell className="text-xs">{t.source}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="relationship" className="mt-4 grid lg:grid-cols-2 gap-4">
          <Card className="p-5">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Target className="h-4 w-4 text-gold" /> Linked Leads</h3>
            {d.leads.length === 0 ? <EmptyState title="No linked leads" /> : d.leads.map((l) => (
              <div key={l.id} className="flex justify-between py-2 border-b last:border-0 border-border text-sm">
                <span>{l.product_interest}</span><StatusBadge status={l.stage} />
              </div>
            ))}
          </Card>
          <Card className="p-5">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Headphones className="h-4 w-4 text-gold" /> Service Requests</h3>
            {d.service_requests.length === 0 ? <EmptyState title="No service requests" /> : d.service_requests.map((s) => (
              <div key={s.id} className="flex justify-between py-2 border-b last:border-0 border-border text-sm">
                <span className="font-num text-xs">{s.ref}</span><span>{s.category}</span><StatusBadge status={s.status} />
              </div>
            ))}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
